import { useState, useEffect, useRef } from 'react';
import { FORCE_EXPERIENCE_MODE } from '@/utils/constants';

const PROBE_URL = '/favicon.ico';
const PROBE_THRESHOLD_MS = 900;
const CACHE_KEY = 'anouk_bandwidth_experience';

export type ExperienceMode = 'full' | 'light';

function getForcedFromUrl(): ExperienceMode | null {
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search).get('experience');
    if (p === 'full' || p === 'light') return p;
    return null;
}

function getCached(): ExperienceMode | null {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw === 'full' || raw === 'light') return raw;
    } catch {
        /* ignore */
    }
    return null;
}

function setCached(mode: ExperienceMode) {
    try {
        sessionStorage.setItem(CACHE_KEY, mode);
    } catch {
        /* ignore */
    }
}

/** Détection combinée : Network Information API + sonde fetch. Forçable via constante ou ?experience=full|light */
export function useBandwidthExperience(): { experienceMode: ExperienceMode; isReady: boolean } {
    const [experienceMode, setExperienceMode] = useState<ExperienceMode>('full');
    const [isReady, setIsReady] = useState(false);
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        const forced = FORCE_EXPERIENCE_MODE ?? getForcedFromUrl();
        if (forced !== null) {
            setExperienceMode(forced);
            setIsReady(true);
            return;
        }

        const cached = getCached();
        if (cached !== null) {
            setExperienceMode(cached);
            setIsReady(true);
            return;
        }

        // 1) Network Information API
        const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
        if (conn) {
            if (conn.saveData === true) {
                setExperienceMode('light');
                setCached('light');
                setIsReady(true);
                return;
            }
            if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') {
                setExperienceMode('light');
                setCached('light');
                setIsReady(true);
                return;
            }
        }

        // 2) Sonde : temps de réponse d’une ressource légère
        const start = performance.now();
        fetch(`${PROBE_URL}?t=${Date.now()}`, { method: 'HEAD', cache: 'no-store' })
            .then(() => {
                const elapsed = performance.now() - start;
                const mode: ExperienceMode = elapsed > PROBE_THRESHOLD_MS ? 'light' : 'full';
                setExperienceMode(mode);
                setCached(mode);
            })
            .catch(() => {
                setExperienceMode('light');
                setCached('light');
            })
            .finally(() => setIsReady(true));
    }, []);

    return { experienceMode, isReady };
}
